import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Globe, XIcon } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { SearchBar } from "@/components/common/search-bar";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import useRegions from "@/features/regions/hooks/use-regions";
import RegionTable from "@/features/regions/ui/region-table";

export default function Regions() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Filter values from URL params (SearchBar manages `search`).
  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";

  const { data, error, isFetching, isLoading } = useRegions(page, search);

  const regions = data?.regions || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handlePageChange = (next: number) => {
    if (next >= 0 && next < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", next.toString());
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Regions..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
            />
          </div>
          <HasAccess permission="write">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate("clone")}>
                Clone region
              </Button>
              <Button className="bg-sidebar" onClick={() => navigate("new")}>
                Add Region
              </Button>
            </div>
          </HasAccess>
        </div>
      }
      data={regions}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load regions"
      emptyState={{
        message: "No regions found matching your criteria",
        icon: <Globe className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <RegionTable
        regions={regions}
        onRowClick={(region) => navigate(region.id)}
      />

      {regions.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
    </ContentLayout>
  );
}
